<template>
  <div class="password_edit" :class="getLevelName()">
    <div class="input_box">
      <label for="passwordEditPassword">
        <i class="xi-lock-o" />
      </label>
      <input
        id="passwordEditPassword" type="password"
        placeholder="PASSWORD"
        :disabled="disabledQualification()"
        v-model="editPasswordForm.current"
        @input="inputValueAni"
      >
    </div>
    <p v-if="qualificationFunction('showCurrentErrorMessage')" class="error_message">
      {{ errorMessage.currentPassword }}
    </p>
    <div class="input_box">
      <label for="passwordEditNewPassword">
        <i class="xi-lock" />
      </label>
      <input
        id="passwordEditNewPassword" type="password"
        placeholder="NEW PASSWORD"
        :disabled="disabledQualification()"
        v-model="editPasswordForm.renew"
        @input="inputValueAni"
        @blur="errorEditPasswordVerification"
      >
    </div>
    <p
      v-if="qualificationFunction('showNewErrorMessage')"
      class="error_message"
    >
      {{ qualificationFunction("showNewErrorMessage") ? errorMessage.newPassword : ""
      }}
    </p>
    <div class="input_box">
      <label for="passwordEditConfirmPassword">
        <i class="xi-lock-o" />
      </label>
      <input
        id="passwordEditConfirmPassword" type="password"
        placeholder="CONFIRM NEW PASSWORD"
        :disabled="disabledQualification()"
        v-model="confirmEditPassword"
        @input="inputValueAni"
        @blur="errorEditPasswordVerification"
      >
    </div>
    <p
      v-if="qualificationFunction('showConfirmErrorMessage')"
      class="error_message"
    >
      {{ qualificationFunction("showConfirmErrorMessage") ? errorMessage.confirmNewPassword : "" }}
    </p>
    <button
      type="button" class="password_edit_submit_btn"
      :class="{ opacity : showModal.isSuccess }"
      :disabled="qualificationFunction('disabledEditPassword')"
      @click="submitEditPassword"
    >
      Edit
    </button>
    <button
      type="button" class="password_edit_cancel_btn"
      :disabled="disabledQualification()"
      @click="closeEditPassword"
    >
      <i class="xi-angle-left" />
      Cancel
    </button>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';

import useCommonStore from '@/store/storeCommon';
import useMemberStore from '@/store/storeMember';

const { isLoading, isMainLogo, isSatelliteInfo } = storeToRefs(useCommonStore());
const { inputValueAni, disabledQualification, showPanel, showModal, modalContents } = useCommonStore();
const { confirmEditPassword } = storeToRefs(useMemberStore());
const { accessEditPassword, errorMessage, editPasswordForm, getLevelName } = useMemberStore();

const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');

/**
 * 수정한 nickname을 저장합니다.
 */
const submitEditPassword = async (): Promise<void> => {
  try {
    isLoading.value = true;

    const response = await accessEditPassword(editPasswordForm, accessToken);

    if (response) {
      showModal.isSuccess = true;
      confirmEditPassword.value = '';

      isMainLogo.value = true;
      isSatelliteInfo.value = false;
      Object.assign(editPasswordForm, {
        current: '',
        renew: '',
      });
      Object.assign(errorMessage, {
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
      Object.assign(modalContents, {
        title: 'Welcome!',
        description: '비밀번호 변경이 성공하였습니다.',
      });
    }
  } catch (e) {
    console.log(e);
  } finally {
    isLoading.value = false;
  }
};

/**
 *  blur 시, 새로 입력한 비밀번호를 확인하고 에러 메세지를 표시합니다.
 */
const errorEditPasswordVerification = (): void => {
  if (editPasswordForm.renew !== confirmEditPassword.value) {
    errorMessage.confirmNewPassword = '비밀번호가 다릅니다.';
  }
  if (!confirmEditPassword.value) {
    errorMessage.confirmNewPassword = '';
  }
  if ((editPasswordForm.current === editPasswordForm.renew) && editPasswordForm.renew) {
    errorMessage.newPassword = '현재 비밀번호와 같습니다.';
  }
};

/**
 *  계정정보 페이지로 이동합니다.
 */
const closeEditPassword = (): void => {
  showPanel.isEditPassword = false;
  confirmEditPassword.value = '';
  Object.assign(editPasswordForm, {
    current: '',
    renew: '',
  });
  Object.assign(errorMessage, {
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
};

/**
 * 조건별 함수
 */
const qualificationFunction = (qualification: string): boolean => {
  switch (qualification) {
    case 'showCurrentErrorMessage':
      return !!(errorMessage.currentPassword && editPasswordForm.current);
    case 'showNewErrorMessage':
      return !!(errorMessage.newPassword && (editPasswordForm.current === editPasswordForm.renew));
    case 'showConfirmErrorMessage':
      return !!(errorMessage.confirmNewPassword && (editPasswordForm.renew !== confirmEditPassword.value));
    case 'disabledEditPassword':
      return disabledQualification() || !(editPasswordForm.current && editPasswordForm.renew) || editPasswordForm.renew !== confirmEditPassword.value || editPasswordForm.current === editPasswordForm.renew;
    default:
      return false;
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/css/_colors.scss';

.password_edit {
  pointer-events: auto;

  .password_edit_submit_btn {
    background: $color-debra;
    width: 100%;
    line-height: 25px;
    border-radius: 5px;
    font-size: 10.2px;
    font-weight: bold;
    color: #2a2a2a;
    margin-top: 30px;

    &:disabled {
      opacity: 0.3;
    }

    &.opacity {
      &:disabled {
        opacity: 1;
      }
    }
  }

  .password_edit_cancel_btn {
    color: $color-white;
    font-size: 12px;
    margin-top: 10px;
    margin-right: auto;
    display: flex;
    align-items: center;
    pointer-events: auto;

    i {
      margin-right: 5px;
    }
  }

  &.GUEST, &.ADMIN, &.METE {
    .input_box {
      &:before {
        background: $color-mete;
      }

      label {
        i {
          color: $color-mete;
        }
      }
    }

    .password_edit_submit_btn {
      background: $color-mete;
    }
  }

  &.DEBRA {
    .input_box {
      &:before {
        background: $color-debra;
      }

      label {
        i {
          color: $color-debra;
        }
      }
    }

    .password_edit_submit_btn {
      background: $color-debra;
    }
  }

  &.KORONG {
    .input_box {
      &:before {
        background: $color-korong;
      }

      label {
        i {
          color: $color-korong;
        }
      }
    }

    .password_edit_submit_btn {
      background: $color-korong;
    }
  }
}

// Surface Duo
@media screen and (min-width: 540px) and (max-height: 720px) and (-webkit-min-device-pixel-ratio: 1.5) {
  .password_edit {
    margin-top: 15px;
  }
}

@media screen and (min-width: 250px) and (max-width: 499px) and (min-height: 450px) {
  .password_edit {
    margin-top: 15px;
  }
}
</style>